<template>
  <Transition name="fade" mode="out-in">
    <!-- CONTENT -->

    <div v-if="!isLoading && isRenderData" key="content" class="flex flex-col">
      <section class="py-8">
        <component :is="currentComponent" v-if="currentComponent" :passSectionItemPost="sectionItemPost"
          :passSectionItemCalendarPost="sectionItemCalendarPost" @refresh="fetchSection" />
        <div v-else>Blank Page</div>
      </section>
    </div>
  </Transition>
</template>

<script>
import { computed, inject, watch, onMounted, ref, provide } from "vue";
import { useRoute } from "vue-router";

import GeneralSection from "@/components/sections/announcements/GeneralSection.vue";
import SystemAdvisorySection from "@/components/sections/announcements/SystemAdvisory.vue";
import CalendarEventSection from "@/components/sections/event-engagement/CalendarEvent.vue";
import CelebrationMilestoneSection from "@/components/sections/event-engagement/CelebrationMilestone.vue";
import HolidayOfficeClosureSection from "@/components/sections/event-engagement/HolidayOfficeClosure.vue";
import CompanyProfile from "@/components/sections/company-overview/CompanyProfile.vue";
import MissionVision from "@/components/sections/company-overview/MissionVision.vue";
import OrganizationalChart from "@/components/sections/company-overview/OrganizationalChart.vue";
import DepartmentPage from "@/components/sections/employees-overview/DepartmentPage.vue";
import EmployeeMasterlist from "@/components/sections/employees-overview/EmployeeMasterlist.vue";
import HMOPage from "@/components/sections/products-and-services/HMOPage.vue";
import GeneralMemorandumAgreement from "@/components/sections/document-directories/OfficeMemorandum.vue";
import JobOpeningPage from "@/components/sections/careers-recruitment/JobOpeningPage.vue";
import BestInAttendance from "@/components/sections/employees-overview/BestInAttendance.vue";
import CompanyPolicy from "@/components/sections/document-directories/CompanyPolicy.vue";
import EmployeeDutiesResponsibilities from "@/components/sections/document-directories/EmployeeDutiesResponsibilities.vue";
import ProcessOfAvailment from "@/components/sections/products-and-services/ProcessOfAvailment.vue";
import ProductsOffered from "@/components/sections/products-and-services/ProductsOffered.vue";
import MOADocuments from "@/components/sections/products-and-services/MOADocuments.vue";
import ProductsPolicy from "@/components/sections/products-and-services/ProductsPolicy.vue";
import PromotionKitAndForms from "@/components/sections/products-and-services/PromotionKitAndForms.vue";
import LicenseAndCertificate from "@/components/sections/document-directories/LicenseAndCertificate.vue";

const componentMap = {
  hmo: HMOPage,
  department: DepartmentPage,
  "general-updates": GeneralSection,
  "system-advisories": SystemAdvisorySection,
  "calendar-event": CalendarEventSection,
  "celebration-milestone": CelebrationMilestoneSection,
  "holiday-office-closure": HolidayOfficeClosureSection,
  "company-profile": CompanyProfile,
  "mission-vision": MissionVision,
  "origanizational-chart": OrganizationalChart,
  "employee-masterlist": EmployeeMasterlist,
  "general-memorandum": GeneralMemorandumAgreement,
  "job-opening": JobOpeningPage,
  "best-in-attendance": BestInAttendance,
  "company-policy": CompanyPolicy,
  "employee-duty-responsibility": EmployeeDutiesResponsibilities,
  "process-availment": ProcessOfAvailment,
  "products-offered": ProductsOffered,
  "memorandum-agreements": MOADocuments,
  "product-policy": ProductsPolicy,
  "promotion-form": PromotionKitAndForms,
  "license-certificate": LicenseAndCertificate,
};

export default {
  components: {},
  setup() {
    const axios = inject("$axios");
    const route = useRoute();

    const module = computed(() => route.params.module);
    const section = computed(() => route.params.section);

    const isLoading = ref(true);
    const isRenderData = ref(false);
    const sectionName = ref("");
    const sectionItemPost = ref([]);
    const sectionItemCalendarPost = ref([]);

    const currentComponent = computed(
      () => componentMap[section.value] || null
    );

    const fetchSection = async () => {
      const sectionId = route.query.sectionId;
      if (!sectionId) {
        console.error("No sectionId in route query");
        return;
      }

      isLoading.value = true;
      isRenderData.value = false;

      try {
        const { data } = await axios.get(`/api/module-sections/${sectionId}`);
        sectionItemPost.value = data.data.modulePosts;
        sectionItemCalendarPost.value = data.data.calendarEvents;
        sectionName.value = data.data.name;
        isRenderData.value = true;
      } catch (error) {
        console.error("render error:", error.response || error);
      } finally {
        isLoading.value = false;
      }
    };

    // provide the function so children can inject it
    provide("refreshSection", fetchSection);

    onMounted(async () => {
      fetchSection();
    });

    watch(
      () => route.query.sectionId,
      (newId, oldId) => {
        if (newId && newId !== oldId) {
          fetchSection(newId);
        }
      }
    );

    return {
      isLoading,
      isRenderData,
      module,
      section,
      currentComponent,
      sectionName,
      sectionItemPost,
      sectionItemCalendarPost,
      fetchSection, // optional if you still want to use it internally
    };
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
